import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import {
  Award,
  Star,
  Gift,
  Trophy,
  TrendingUp,
  Ticket,
  Clock,
  CheckCircle,
  ChevronRight,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';

const TIER_BENEFITS = {
  SILVER: [
    'Late checkout (subject to availability)',
    '10% discount on spa services',
    'Welcome drink on arrival',
  ],
  GOLD: [
    'Guaranteed late checkout (2pm)',
    'Room upgrade (subject to availability)',
    '15% discount on spa services',
    'Welcome amenity basket',
    'Priority reservation',
  ],
  PLATINUM: [
    'Guaranteed room upgrade',
    'Complimentary late checkout (4pm)',
    'Free breakfast daily',
    '25% discount on spa services',
    'Airport transfer (one-way)',
    'Exclusive lounge access',
    'Personal concierge',
  ],
};

const SAMPLE_REWARDS = [
  { id: 1, name: 'Free Night Award', points: 10000, category: 'Stay', description: 'Redeem for one free night at any property' },
  { id: 2, name: 'Spa Credit', points: 2500, category: 'Spa', description: '5,000 ETB spa credit' },
  { id: 3, name: 'Room Upgrade', points: 1500, category: 'Stay', description: 'Upgrade to next room category' },
  { id: 4, name: 'Airport Transfer', points: 2000, category: 'Transport', description: 'Round-trip airport transfer' },
  { id: 5, name: 'Dining Credit', points: 1000, category: 'Dining', description: '2,000 ETB restaurant credit' },
  { id: 6, name: 'Late Checkout', points: 500, category: 'Stay', description: 'Extend checkout to 4pm' },
];

const SAMPLE_HISTORY = [
  { id: 1, description: 'Stay at Grand Addis Hotel', points: 1500, date: '2024-12-15', type: 'earn' },
  { id: 2, description: 'Spa treatment', points: 250, date: '2024-12-16', type: 'earn' },
  { id: 3, description: 'Dining Credit Redemption', points: -1000, date: '2024-12-18', type: 'redeem' },
  { id: 4, description: 'Stay at Harar Heritage Inn', points: 800, date: '2024-11-20', type: 'earn' },
];

export default function GuestLoyaltyPage() {
  const navigate = useNavigate();
  const { token, guest, isAuthenticated, isLoading: authLoading } = useGuestAuth();
  const [loading, setLoading] = useState(true);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [history, setHistory] = useState([]);
  const [redeeming, setRedeeming] = useState(null);
  const [enrolled, setEnrolled] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }
    
    // Only load data when we have a token
    if (token) {
      loadLoyaltyData();
    }
  }, [authLoading, isAuthenticated, navigate, token]);

  const loadLoyaltyData = async () => {
    if (!token) {
      console.log('No token available for loyalty data');
      return;
    }
    
    try {
      setLoading(true);
      
      // Fetch loyalty status from backend
      const [statusRes, rewardsRes, historyRes] = await Promise.all([
        guestPortalApi.getLoyaltyStatus(token),
        guestPortalApi.getAvailableRewards(token),
        guestPortalApi.getLoyaltyTransactions(token)
      ]);
      
      if (!statusRes.data.enrolled) {
        setEnrolled(false);
        setLoading(false);
        return;
      }
      
      // Map backend data to component format
      const status = statusRes.data;
      setLoyaltyData({
        tier: status.tier_code || status.tier?.toUpperCase() || 'SILVER',
        tierName: status.tier || 'Member',
        points: status.points_balance,
        pointsToNextTier: status.points_to_next_tier,
        nextTier: status.next_tier,
        totalNights: status.tier_qualifying_nights || 0,
        memberSince: status.enrollment_date,
        memberNumber: status.member_number,
        lifetimePoints: status.lifetime_points,
        programName: status.program_name,
      });
      
      // Set rewards
      setRewards(rewardsRes.data || []);
      
      // Map history
      const mappedHistory = (historyRes.data || []).map(txn => ({
        id: txn.id,
        description: txn.description,
        points: txn.points,
        date: txn.created_at?.split('T')[0],
        type: txn.points > 0 ? 'earn' : 'redeem'
      }));
      setHistory(mappedHistory);
      
    } catch (error) {
      console.error('Failed to load loyalty data:', error);
      // Fallback to sample data if API fails
      setLoyaltyData({
        tier: 'GOLD',
        tierName: 'Gold',
        points: 8750,
        pointsToNextTier: 11250,
        nextTier: 'Platinum',
        totalNights: 15,
        memberSince: '2023-06-15',
      });
      setRewards(SAMPLE_REWARDS);
      setHistory(SAMPLE_HISTORY);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemReward = async (reward) => {
    if (loyaltyData.points < reward.points) {
      toast.error('Not enough points for this reward');
      return;
    }
    
    setRedeeming(reward.id);
    
    try {
      const response = await guestPortalApi.redeemReward(token, reward.id);
      
      // Update local state with new balance
      setLoyaltyData(prev => ({
        ...prev,
        points: response.data.new_balance,
      }));
      
      // Refresh history
      const historyRes = await guestPortalApi.getLoyaltyTransactions(token);
      const mappedHistory = (historyRes.data || []).map(txn => ({
        id: txn.id,
        description: txn.description,
        points: txn.points,
        date: txn.created_at?.split('T')[0],
        type: txn.points > 0 ? 'earn' : 'redeem'
      }));
      setHistory(mappedHistory);
      
      toast.success(`Successfully redeemed ${reward.name}!`);
    } catch (error) {
      console.error('Failed to redeem reward:', error);
      toast.error(error.response?.data?.detail || 'Failed to redeem reward');
    } finally {
      setRedeeming(null);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'SILVER': return 'bg-slate-200 text-slate-700';
      case 'GOLD': return 'bg-amber-100 text-amber-700';
      case 'PLATINUM': return 'bg-gradient-to-r from-slate-200 to-slate-400 text-slate-800';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getProgressToNextTier = () => {
    if (!loyaltyData) return 0;
    const total = loyaltyData.points + loyaltyData.pointsToNextTier;
    return (loyaltyData.points / total) * 100;
  };

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Skeleton className="h-10 w-48 mb-8" />
        <Skeleton className="h-48 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  if (!enrolled) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-2xl text-center" data-testid="not-enrolled">
        <Award className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
        <h1 className="text-2xl font-bold mb-4">Join Our Rewards Program</h1>
        <p className="text-muted-foreground mb-6">
          You're not yet enrolled in our loyalty program. Join today to start earning points on every stay!
        </p>
        <Button onClick={() => navigate('/portal/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl" data-testid="guest-loyalty-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Loyalty & Rewards</h1>
        <p className="text-muted-foreground">Earn points on every stay and redeem for exclusive rewards</p>
      </div>

      {/* Loyalty Status Card */}
      <Card className="mb-8 overflow-hidden">
        <div className="bg-gradient-to-br from-amber-500 via-amber-400 to-yellow-400 dark:from-amber-700 dark:via-amber-600 dark:to-yellow-600 p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <Badge className={getTierColor(loyaltyData?.tier)}>
                <Trophy className="h-3 w-3 mr-1" />
                {loyaltyData?.tierName || loyaltyData?.tier} Member
              </Badge>
              <p className="text-sm opacity-80 mt-2">Member since {loyaltyData?.memberSince ? new Date(loyaltyData.memberSince).toLocaleDateString() : 'N/A'}</p>
              {loyaltyData?.memberNumber && (
                <p className="text-xs opacity-60 mt-1">Member # {loyaltyData.memberNumber}</p>
              )}
            </div>
            <Award className="h-16 w-16 opacity-20" />
          </div>
          <div className="mt-6">
            <p className="text-sm opacity-80">Available Points</p>
            <p className="text-5xl font-bold">{loyaltyData?.points?.toLocaleString()}</p>
          </div>
        </div>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {loyaltyData?.nextTier && loyaltyData?.pointsToNextTier && (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>{loyaltyData?.tierName || loyaltyData?.tier}</span>
                  <span className="text-muted-foreground">{loyaltyData.pointsToNextTier.toLocaleString()} pts to {loyaltyData.nextTier}</span>
                </div>
                <Progress value={getProgressToNextTier()} className="h-2" />
              </div>
            )}
            
            {loyaltyData?.expiringPoints > 0 && (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                <Clock className="h-4 w-4" />
                <span>{loyaltyData.expiringPoints.toLocaleString()} points expiring on {new Date(loyaltyData.expiryDate).toLocaleDateString()}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-2xl font-bold">{loyaltyData?.totalNights}</p>
                <p className="text-sm text-muted-foreground">Total Nights</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-2xl font-bold">5</p>
                <p className="text-sm text-muted-foreground">Nights to Gold</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="rewards" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="rewards">Redeem Rewards</TabsTrigger>
          <TabsTrigger value="benefits">Your Benefits</TabsTrigger>
          <TabsTrigger value="history">Activity</TabsTrigger>
        </TabsList>

        {/* Rewards Tab */}
        <TabsContent value="rewards" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rewards.map(reward => (
              <Card key={reward.id} className="hover:shadow-md transition-shadow" data-testid={`reward-${reward.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <Badge variant="outline">{reward.category}</Badge>
                    <div className="text-right">
                      <span className="font-bold text-lg">{reward.points.toLocaleString()}</span>
                      <span className="text-sm text-muted-foreground"> pts</span>
                    </div>
                  </div>
                  <CardTitle className="text-lg mt-2">{reward.name}</CardTitle>
                  <CardDescription>{reward.description}</CardDescription>
                </CardHeader>
                <CardFooter>
                  <Button 
                    className="w-full" 
                    disabled={loyaltyData.points < reward.points || redeeming === reward.id}
                    onClick={() => handleRedeemReward(reward)}
                    data-testid={`redeem-${reward.id}`}
                  >
                    {redeeming === reward.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Gift className="h-4 w-4 mr-2" />
                    )}
                    {loyaltyData.points >= reward.points ? 'Redeem Now' : 'Not Enough Points'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Benefits Tab */}
        <TabsContent value="benefits">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                {loyaltyData?.tier} Member Benefits
              </CardTitle>
              <CardDescription>Enjoy these exclusive perks with your membership</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {TIER_BENEFITS[loyaltyData?.tier]?.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </CardContent>
            {loyaltyData?.tier !== 'PLATINUM' && (
              <CardFooter>
                <div className="w-full p-4 bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 rounded-lg">
                  <p className="text-sm font-medium mb-2">Upgrade to {loyaltyData?.tier === 'GOLD' ? 'PLATINUM' : 'GOLD'} to unlock:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {TIER_BENEFITS[loyaltyData?.tier === 'GOLD' ? 'PLATINUM' : 'GOLD']?.slice(0, 3).map((benefit, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <Star className="h-3 w-3" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardFooter>
            )}
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Points Activity</CardTitle>
              <CardDescription>Your recent earning and redemption history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {history.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${item.type === 'earn' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                        {item.type === 'earn' ? (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : (
                          <Gift className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{item.description}</p>
                        <p className="text-sm text-muted-foreground">{new Date(item.date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <span className={`font-bold ${item.points > 0 ? 'text-green-600' : 'text-blue-600'}`}>
                      {item.points > 0 ? '+' : ''}{item.points.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
